import React, { Component } from 'react'
import { Modal } from 'react-bootstrap'
import { TagPicker } from '../../exams/common/QuestionTools'
import '../../exams/common/_questionTools.scss'

// Notes modal for revision materials, short notes, notes and bookmark screens.
// Same look as the exam question tools (QuestionTools); the parent still saves
// through noteshandleFormSubmit and reports the outcome in its own state
// (currentStep 5 = saved, submitError2 = error), so no parent changes.
//
// Notes used to be written in a rich-text editor, so stored comments are HTML.
// They are edited here as plain text: line breaks are kept as <br />, and any
// images in an existing note are kept and re-attached on save.

const decode = (s) => {
    try { return decodeURIComponent(s); } catch (e) { return s; }
};

const htmlToText = (html) => {
    if (!html) return "";
    const marked = String(decode(html))
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
        .replace(/<img[^>]*>/gi, "");
    const doc = new DOMParser().parseFromString(marked, "text/html");
    return (doc.body.textContent || "").replace(/ /g, " ").replace(/\n{3,}/g, "\n\n").trim();
};

const imagesOf = (html) => (html ? String(decode(html)).match(/<img[^>]*>/gi) || [] : []);

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const textToHtml = (text, images) => escapeHtml(text.trim()).replace(/\r?\n/g, "<br />") + images.join("");

const errorOf = (e) => (Array.isArray(e) ? e.join(", ") : e ? String(e) : "");

class NoteForm extends Component {
    constructor(props) {
        super(props);
        const tags = props.tags;
        const own = new Set(tags.map((t) => String(t.id)));
        const selected = String(props.initialTags || "").split(",").map((x) => x.trim()).filter((x) => own.has(x));
        this.images = imagesOf(props.initialComments);
        this.state = {
            tagMode: selected.length > 0 || tags.length > 0 ? "existing" : "new",
            selected,
            newTag: "",
            comment: htmlToText(props.initialComments),
            error: "",
            saving: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (!this.state.saving) return;
        if (prevProps.serverError !== this.props.serverError && errorOf(this.props.serverError)) {
            this.setState({ saving: false, error: errorOf(this.props.serverError) });
        }
    }

    tagPayload() {
        const { tagMode, selected, newTag } = this.state;
        if (tagMode === "existing") return { tags: selected.join(","), new_tag: "" };
        const name = newTag.trim();
        const match = this.props.tags.find((t) => String(t.tag).trim().toLowerCase() === name.toLowerCase());
        return match ? { tags: String(match.id), new_tag: "" } : { tags: "", new_tag: name };
    }

    submit = () => {
        const { tagMode, selected, newTag, comment } = this.state;
        if (tagMode === "existing" && selected.length === 0) return this.setState({ error: "Select at least one tag." });
        if (tagMode === "new" && !newTag.trim()) return this.setState({ error: "Type a name for the new tag." });
        if (!comment.trim() && this.images.length === 0) return this.setState({ error: "Write your note." });
        const { tags, new_tag } = this.tagPayload();
        this.setState({ saving: true, error: "" });
        this.props.onSave(tags, new_tag, textToHtml(comment, this.images));
    };

    render() {
        const { tags, saved, onClose } = this.props;
        const { tagMode, selected, newTag, comment, error, saving } = this.state;
        return (
            <div className="qt-form">
                <div className="qt-head">
                    <span className="qt-head-icon qt-head-icon--notes"><i className="fas fa-sticky-note" /></span>
                    <h6 className="qt-title">{this.props.editing ? "Edit note" : "Add a note"}</h6>
                    <button type="button" className="qt-close" aria-label="Close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>

                {saved ? (
                    <div className="qt-done"><i className="fas fa-check-circle" /> Note saved.</div>
                ) : (
                    <>
                        <div className="qt-body">
                            <TagPicker
                                label="Your note tags"
                                tags={tags}
                                selected={selected}
                                mode={tagMode}
                                onMode={(m) => this.setState({ tagMode: m, error: "" })}
                                newTag={newTag}
                                onSetSelected={(ids) => this.setState({ selected: ids, error: "" })}
                                onNewTag={(name) => this.setState({ newTag: name, error: "" })}
                            />
                            <div className="qt-field">
                                <div className="qt-label">Note</div>
                                <textarea
                                    className="qt-input qt-textarea"
                                    rows={6}
                                    placeholder="Write your note"
                                    value={comment}
                                    onChange={(e) => this.setState({ comment: e.target.value, error: "" })}
                                />
                                {this.images.length > 0 ? (
                                    <div className="qt-hint mt-2">
                                        <i className="far fa-image" /> {this.images.length} image{this.images.length > 1 ? "s" : ""} in this note will be kept.
                                    </div>
                                ) : null}
                            </div>
                            {error ? <div className="qt-error" role="alert"><i className="fas fa-exclamation-circle" /> {error}</div> : null}
                        </div>
                        <div className="qt-foot">
                            <button type="button" className="qt-btn qt-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="button" className="qt-btn qt-btn--primary" onClick={this.submit} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

class SingleNoteModal extends Component {
    cancel = () => this.props.onHide(this.props.stateData.getDerived);

    save = (tags, newTag, comments) => {
        const { stateData, removecontypId, custonid } = this.props;
        this.props.noteshandleFormSubmit(tags, newTag, comments, removecontypId, custonid, stateData.getDerived);
    };

    render() {
        const { show, stateData, studentGlobals = {}, custonid } = this.props;
        const tags = (studentGlobals.tags || []).filter((t) => t && t.type === "notes");
        const editing = !!(stateData.ntags || stateData.ncomments);
        return (
            <Modal show={show} onHide={this.cancel} centered className="qt-modal qt-modal--wide" aria-label="Notes">
                {/* key: reopening (getDerived bumps) or another item starts a fresh form */}
                <NoteForm
                    key={custonid + "-" + stateData.getDerived}
                    tags={tags}
                    editing={editing}
                    initialTags={stateData.ntags}
                    initialComments={stateData.ncomments}
                    saved={stateData.currentStep == 5}
                    serverError={stateData.submitError2}
                    onSave={this.save}
                    onClose={this.cancel}
                />
            </Modal>
        );
    }
}

export default SingleNoteModal;
